/**
 * Analytics helpers — Umami (ambient-telemetry) + Google Ads conversion
 * tracking + Meta Pixel + Internal Self-Hosted Tracker.
 *
 * Designed to be:
 *  - SSR-safe (all side-effects guarded by typeof window check).
 *  - Load-on-consent: call initAnalytics() only after the user accepts the
 *    consent banner. If an ID is missing the corresponding tracker is
 *    silently skipped.
 *
 * IDs come from Vite environment variables:
 *   VITE_GOOGLE_ADS_CONVERSION_ID      e.g. AW-XXXXXXXXX
 *   VITE_GOOGLE_ADS_CONVERSION_LABEL   e.g. F20yCJe6ttYbEI3K-7RC
 *   VITE_META_PIXEL_ID                 e.g. 1234567890
 *   VITE_API_URL                       e.g. http://localhost:8787 or https://api.example.com
 */

import { initInternalAnalytics, trackInternalEvent } from './internalAnalytics';
import type { AnalyticsEventName, BrowserAnalyticsEvent } from '@tilly/shared';
import { organicAttributionParams } from './organicAttribution';
import { trackTelemetryEvent } from '@/lib/analytics/telemetry';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      push?: (...args: unknown[]) => void;
    };
    _fbq?: Window['fbq'];
  }
}

// ─── Google Consent Mode v2 ──────────────────────────────────────────────
//
// The Google tag (gtag.js) is loaded unconditionally from the static
// index.html <head> (Google Ads conversion tracking only — GA4 is no longer
// used) with all consent signals defaulting to 'denied'.  When
// the user accepts the consent banner, initAnalytics() calls
// updateGoogleConsent(true) to lift the block — Google then activates
// measurement and replays any queued events.
//
// We no longer dynamically inject a second gtag.js script; the static tag
// in index.html is the single source of truth for the dataLayer.  This also
// satisfies Google Ads' tag-verification crawler, which checks for the
// script's presence in the raw HTML.
function updateGoogleConsent(granted: boolean): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  const status = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
    analytics_storage: status,
  });
}

function loadGoogleTag(adsId: string): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.gtag ??= (...args: unknown[]) => window.dataLayer.push(args);

  updateGoogleConsent(true);
  if (!document.getElementById('tilly-google-tag')) {
    const script = document.createElement('script');
    script.id = 'tilly-google-tag';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`;
    document.head.appendChild(script);
  }

  window.gtag('js', new Date());
  window.gtag('config', adsId);
}

// ─── Meta Pixel ──────────────────────────────────────────────────────────

function loadMetaPixel(pixelId: string): void {
  if (typeof window === 'undefined') return;
  if (window.fbq) return; // already loaded

  // Standard Meta Pixel stub — properties assigned after function declaration
  // to avoid the Temporal Dead Zone bug that `Object.assign` + getter causes
  // when the variable is a `const` (getter is invoked during Object.assign
  // before the binding is complete, crashing in minified production builds).
  const queue: unknown[] = [];

  function fbq(...args: unknown[]): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((fbq as any).callMethod) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fbq as any).callMethod(...args);
    } else {
      queue.push(args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const f = fbq as any;
  f.push = fbq;
  f.loaded = true;
  f.version = '2.0';
  f.queue = queue;

  window.fbq = f;
  window._fbq = f;

  f('init', pixelId);
  f('track', 'PageView');

  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  // Noscript fallback (pixel tracking image)
  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.insertAdjacentElement('afterbegin', noscript);
}

// ─── Public API ──────────────────────────────────────────────────────────

let analyticsInitialized = false;

/** Load all configured trackers. Safe to call multiple times (idempotent). */
export function initAnalytics(): void {
  if (analyticsInitialized) return;
  analyticsInitialized = true;
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  const adsId = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID;
  const apiUrl = import.meta.env.VITE_API_URL || '';

  // Internal-traffic flag read by the self-hosted tracker (organicAttribution.ts).
  const params = new URLSearchParams(window.location.search);
  if (params.get('analytics_internal') === '1')
    localStorage.setItem('tilly_analytics_internal', 'true');
  if (params.get('analytics_internal') === '0') localStorage.removeItem('tilly_analytics_internal');

  // Lift the Consent Mode v2 block set in index.html.
  // The static gtag.js is already loaded; this call activates measurement and
  // replays any conversion events that were queued while consent was 'denied'.
  if (adsId) loadGoogleTag(adsId);

  if (pixelId) loadMetaPixel(pixelId);

  // Initialize internal self-hosted tracker.
  // When VITE_API_URL is unset (local dev with Vite proxy), use a relative path so
  // the proxy forwards /api → localhost:8787.  When set, prepend /api explicitly.
  const ingestUrl = apiUrl
    ? `${apiUrl}/api/public/analytics/events`
    : '/api/public/analytics/events';
  initInternalAnalytics(ingestUrl);
}

/**
 * Track experiment view event (consent-gated).
 * Fires only if consent has been granted.
 */
export function trackExperimentView(experimentId: string): void {
  if (typeof window === 'undefined') return;

  // Check consent status via localStorage
  const consent = localStorage.getItem('tilly_consent');
  if (consent !== 'granted') return;

  trackTelemetryEvent('view_experiment', { experiment_id: experimentId });

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'ViewExperiment', {
      experiment_id: experimentId,
    });
  }

  // Internal tracker
  trackInternalEvent('experiment_view', {
    experiment_id: experimentId,
  });
}

/**
 * Track SEO landing page view event (consent-gated).
 * Fires only if consent has been granted.
 * Mirrors trackExperimentView — sends seo_page_id to Umami, Meta Pixel, and internal tracker
 * so conversions can be attributed to a specific SEO page in reports.
 */
export function trackSeoPageView(seoPageId: string): void {
  if (typeof window === 'undefined') return;

  const consent = localStorage.getItem('tilly_consent');
  if (consent !== 'granted') return;

  trackTelemetryEvent('view_seo_page', { seo_page_id: seoPageId });

  // Meta Pixel
  if (window.fbq) {
    window.fbq('trackCustom', 'ViewSeoPage', {
      seo_page_id: seoPageId,
    });
  }

  // Internal tracker
  trackInternalEvent('seo_page_view', {
    seo_page_id: seoPageId,
  });
}

/**
 * Generic event tracking (consent-gated).
 * Usage: trackEvent('cta_click', { button_name: 'primary', experiment_id: 'salon-pain' })
 */
export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const consent = localStorage.getItem('tilly_consent');
  if (consent !== 'granted') return;

  trackTelemetryEvent(eventName, params);

  if (window.fbq) {
    window.fbq('trackCustom', eventName, params ?? {});
  }

  // Also track in internal tracker (map event name intelligently if needed)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackInternalEvent(eventName as any, params);
}

/** Track a typed organic-funnel event in Umami and the self-hosted pipeline. */
export function trackOrganicEvent(
  eventName: AnalyticsEventName,
  params?: Record<string, unknown>
): void {
  const eventId = crypto.randomUUID();
  trackEvent(eventName, {
    event_id: eventId,
    ...organicAttributionParams(),
    ...(params ?? {}),
  });
}

/** Mirror an authoritative server lifecycle event to Umami without duplicating internal storage. */
export function trackBrowserLifecycleEvents(events: BrowserAnalyticsEvent[]): void {
  if (typeof window === 'undefined' || localStorage.getItem('tilly_consent') !== 'granted') return;
  for (const event of events) {
    trackTelemetryEvent(event.name, { event_id: event.event_id, ...event.params });
  }
}

/**
 * Report a Google Ads conversion (equivalent to Google's `gtag_report_conversion`
 * snippet). Fires the `conversion` event for the configured AW-.../label pair,
 * then navigates to `url` (via event_callback, same as Google's own snippet) so
 * the conversion beacon has a chance to send before leaving the page.
 *
 * Usage: `<a onClick={(e) => { e.preventDefault(); reportConversion(href); }}>`
 *
 * Returns `false` (matching Google's `return gtag_report_conversion(url)`
 * pattern) so callers used directly as an onClick handler cancel default nav.
 */
export function reportConversion(url?: string): boolean {
  const navigate = () => {
    if (url) window.location.href = url;
  };

  if (typeof window === 'undefined') {
    navigate();
    return false;
  }

  const consent = localStorage.getItem('tilly_consent');
  const conversionId = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID;
  const conversionLabel = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL;

  if (consent !== 'granted' || !window.gtag || !conversionId || !conversionLabel) {
    navigate();
    return false;
  }

  window.gtag('event', 'conversion', {
    send_to: `${conversionId}/${conversionLabel}`,
    value: 1.0,
    currency: 'USD',
    event_callback: navigate,
  });

  return false;
}
